import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useChatApi } from '../Services/ChatApiProvider';

import assistantAvatar1 from '../media/assist1.svg';

const AskContainer = ({ onClick, isChatActive }) => {
  // normalize raw base64 / id → data URL
  const toDataUrl = (s) => {
    if (!s || typeof s !== 'string') return null;
    const compact = s.trim().replace(/\s+/g, '');
    if (!compact || compact === 'null' || compact === 'undefined') return null;
    return compact.startsWith('data:image/') ? compact : `data:image/png;base64,${compact}`;
  };

  const { messages } = useChatApi();

  const [lastMessage, setLastMessage] = useState(null);
  const [hasMessages, setHasMessages] = useState(false);

  const { channelSettings } = useChatApi();
  const aiAssistantImage = toDataUrl(channelSettings?.AIassistantImage);
  const liveAssistantImage = toDataUrl(channelSettings?.liveAssistantImage);

  const ask1 = (channelSettings?.askBoxFirstLine ?? '').toString().trim() || 'Чат';
  const ask2 = (channelSettings?.askBoxSecondLine ?? '').toString().trim() || 'Задати питання';
  const ask3 =
    (channelSettings?.askBoxThirdLine ?? '').toString().trim() ||
    'ШІ асистент та команда допоможуть';

  useEffect(() => {
    setHasMessages(messages?.some((msg) => msg.sender === 'assistant' || msg.sender === 'user'));
    setLastMessage(messages?.length && messages?.length > 0 ? messages[messages.length - 1] : null);
  }, [messages]);

  // Helper function to validate if a message is a real message (not intro/system)
  const isValidMessage = (msg) => {
    if (!msg) return false; // No message at all

    // Exclude system/intro messages that don’t contain real content
    if (msg.intro || msg.newassistant || msg.subscription) {
      return false;
    }

    // Check if the message contains valid content (text, images, audio, video, file)
    return !!(msg.text || msg.images || msg.file || msg.audio || msg.video);
  };

  const getAvatar = (msg) => {
    if (!msg) return null;
    if (msg.sender === 'user') {
      return (
        <motion.div
          className="avatar user"
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.5 }}
          transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15,
            duration: 0.1,
          }}
        />
      );
    } else if (msg.type === 'bot') {
      // If we have a configured AI assistant image, paint it; otherwise use default masked bot
      if (aiAssistantImage) {
        return (
          <motion.div
            className="avatar bot has-ai-avatar"
            style={{ backgroundImage: `url(${aiAssistantImage})` }}
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            transition={{ type: 'spring', stiffness: 200, damping: 15, duration: 0.1 }}
          />
        );
      }

      return (
        <motion.div
          className="avatar bot"
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.5 }}
          transition={{ type: 'spring', stiffness: 200, damping: 15, duration: 0.1 }}
        />
      );
    } else {
      return (
        <motion.img
          className="message-avatar"
          src={liveAssistantImage || assistantAvatar1}
          alt={msg.name || 'Assistant'}
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.5 }}
          transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15,
            duration: 0.1,
          }}
        />
      );
    }
  };

  const formatMessage = (text) => {
    return text.length > 28 ? text.substring(0, 24) + '...' : text;
  };

  const getTimeAgo = (timestamp) => {
    const messageDateTime = new Date(timestamp);
    const now = new Date();

    const diffInMinutes = Math.floor((now - messageDateTime) / (1000 * 60));

    if (diffInMinutes < 60) {
      return `${diffInMinutes} min ago`;
    } else if (diffInMinutes < 120) {
      return `1 hour ago`;
    } else if (diffInMinutes < 1440) {
      return `${Math.floor(diffInMinutes / 60)} hours ago`;
    } else if (diffInMinutes < 2880) {
      return `Yesterday`;
    } else {
      return `${Math.floor(diffInMinutes / 1440)} days ago`;
    }
  };

  return (
    <AnimatePresence>
      {!isChatActive && (
        <motion.div
          className={`ask-container ${lastMessage && isValidMessage(lastMessage) ? '' : 'chat-bubble'}`}
          onClick={onClick}
          initial={{ y: -5, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -5, opacity: 0 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
        >
          {hasMessages ? (
            <>
              <h3>{ask1}</h3>
              <div className="last-chat-message">
                {getAvatar(lastMessage)}
                <div className="last-message">
                  <div
                    className="message-line"
                    dangerouslySetInnerHTML={{ __html: formatMessage(lastMessage?.text) || '' }}
                  />
                  <div className="message-subscription">
                    {lastMessage?.sender === 'user'
                      ? 'You'
                      : lastMessage?.type === 'bot'
                        ? 'SURE Support Bot'
                        : lastMessage?.name}
                    <span className="dot"></span>
                    <span className="time">{getTimeAgo(lastMessage?.timestamp)}</span>
                  </div>
                </div>
                <div className="shevron-btn"></div>
              </div>
            </>
          ) : (
            <>
              <h3>{ask1}</h3>
              <span className="no-messages-title">{ask2}</span>
              <span className="no-messages-badge">{ask3}</span>
            </>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AskContainer;
